import { atom } from 'recoil';

import type { GetCandidateV2Response } from 'client/types.gen';
import type { AutotagRationale } from 'components/candidate/lib/candidate-autotag.schemas';
import { atomEffectLocalStorage } from 'helpers/atoms';
import type { ClientState } from 'types/recoil';
import { Atoms } from 'types/recoil';

export const atomForm = atom<ClientState[Atoms.Form]>({
  default: undefined,
  key: Atoms.Form,
});

export const atomModal = atom<ClientState[Atoms.Modal]>({
  default: undefined,
  key: Atoms.Modal,
});

export const atomRecommendations = atom<ClientState[Atoms.Recommendations]>({
  default: {},
  effects: [atomEffectLocalStorage()],
  key: Atoms.Recommendations,
});

// Candidate-shaped seed handed from the "Import Tier 3 candidate" dialog to
// /candidates/new so the create form opens pre-filled with the autotag proposal,
// plus the per-tag rationale that drives the review panel.
export interface AutotagSeed {
  values: Partial<GetCandidateV2Response>;
  rationales: AutotagRationale[];
}

export const atomAutotagSeed = atom<AutotagSeed | undefined>({
  default: undefined,
  key: 'atomAutotagSeed',
});

type AtomDialog = 'atomDialog';

export type Dialog =
  | {
      id: 'add-to-pipeline';
    }
  | {
      data: {
        name: string;
        teamsCount: number;
      };
      id: 'delete-trending-submission';
    }
  | {
      data: {
        name: string;
        teamsCount: number;
      };
      id: 'cancel-trending-submission';
    }
  | {
      id: 'introduction-feedback';
    }
  | {
      id: 'introduction-feedback-success';
    }
  | {
      id: 'introduction-request';
    }
  | {
      id: 'introduction-request-success';
    }
  | {
      id: 'people-filters';
    }
  | {
      id: 'recommendation-people-filters';
    }
  | {
      data: {
        name: string;
        recommendationUuid: string;
        teamUuid: string;
      };
      id: 'recommendation-comments';
    }
  | {
      data: {
        recommendationUuid: string;
        submissionUuid?: string;
      };
      id: 'recommendation-delete';
    }
  | {
      data: {
        name: string;
        teamUuid: string;
      };
      id: 'new-trending-recommendation-remove';
    }
  | {
      data: {
        recommendationUuid: string;
      };
      id: 'recommendation-edit';
    }
  | {
      id: 'submission-publish';
    }
  | {
      id: undefined;
    };

export type ExtractDataType<T, U> = T extends { data: infer D; id: U }
  ? D
  : undefined;

export const atomDialog = atom<Dialog>({
  default: {
    id: undefined,
  },
  key: 'atomDialog' satisfies AtomDialog,
});

export const atoms: Record<Atoms, any> = {
  atomForm,
  atomModal,
  atomRecommendations,
};
